import { useState, useEffect, useMemo } from 'react';
import { SIMPLE_TAG_SAMPLES, THEME_TAGS } from '@/lib/themeTags';
import { findInvalidThemeTags, findForeignThemeSyntax } from '@/lib/themeTagValidation';
import { parseBlockSchema } from '@/lib/themeBlocks';

interface PageDef {
  key: string;
  label: string;
  icon: string;
}

interface ThemePage {
  id: number;
  idtheme: number;
  page_key: string;
  title: string;
  content: string;
}

interface LivePreviewPanelProps {
  stylesheet: string;
  pages: ThemePage[];
  pageDefs: PageDef[];
}

const TAG_REGEX = /\{\{\s*([\w-]+)((?:\s+\w+="[^"]*")*)\s*\}\}/g;

/** Petit rendu d'exemple pour une balise de bloc, indépendant du CSS du thème. */
function blockPlaceholder(name: string, attrs: Record<string, string>): string {
  const limit = parseInt(attrs.limit || '3', 10) || 3;
  const count = Math.min(Math.max(limit, 2), 8);
  const cards = Array.from({ length: count }).map((_, i) => `
    <div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;overflow:hidden;background:#fff;">
      <div style="height:130px;background:linear-gradient(135deg,#eee,#ddd);display:flex;align-items:center;justify-content:center;color:#999;font-size:22px;">${i % 2 ? '🛍️' : '🔧'}</div>
      <div style="padding:12px;">
        <div style="height:10px;width:70%;background:#e5e5e5;border-radius:4px;margin-bottom:8px;"></div>
        <div style="height:10px;width:40%;background:#eee;border-radius:4px;"></div>
      </div>
    </div>`).join('');

  const wrap = (inner: string) => `<div style="margin:8px 0;">${inner}</div>`;
  const grid = (cols: number) => `<div style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:16px;">${cards}</div>`;

  switch (name) {
    case 'hero':
      return wrap(`<div style="border-radius:16px;overflow:hidden;height:260px;background:linear-gradient(135deg,#c9b8a8,#8a7a6a);display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px;color:#fff;"><div style="height:16px;width:260px;background:rgba(255,255,255,.85);border-radius:6px;"></div><div style="height:10px;width:180px;background:rgba(255,255,255,.5);border-radius:6px;"></div><div style="height:40px;width:150px;background:rgba(255,255,255,.9);border-radius:9999px;margin-top:6px;"></div></div>`);
    case 'heading':
      return wrap(`<div style="height:24px;width:50%;background:#ddd;border-radius:6px;margin:14px 0;"></div>`);
    case 'text':
      return wrap(`<div>${Array.from({ length: 3 }).map(() => '<div style="height:10px;background:#eee;border-radius:4px;margin-bottom:8px;"></div>').join('')}<div style="height:10px;width:60%;background:#eee;border-radius:4px;"></div></div>`);
    case 'image':
      return wrap(`<div style="height:200px;background:linear-gradient(135deg,#eee,#ddd);border-radius:16px;display:flex;align-items:center;justify-content:center;color:#aaa;font-size:26px;">🖼️</div>`);
    case 'cta':
      return wrap(`<div style="border-radius:16px;background:#333;color:#fff;padding:32px;text-align:center;"><div style="height:14px;width:220px;background:rgba(255,255,255,.8);border-radius:6px;margin:0 auto 12px;"></div><div style="height:38px;width:140px;background:rgba(255,255,255,.9);border-radius:9999px;margin:0 auto;"></div></div>`);
    case 'spacer':
      return `<div style="height:${parseInt(attrs.height || '48', 10) || 48}px;"></div>`;
    case 'divider':
      return wrap('<hr style="border:none;border-top:1px solid rgba(0,0,0,.1);">');
    case 'products':
    case 'featured_products':
    case 'marketplace_products':
      return wrap(grid(3));
    case 'services':
      return wrap(grid(3));
    case 'blog_posts':
      return wrap(grid(3));
    case 'portfolio':
    case 'gallery':
      return wrap(grid(4));
    case 'partners':
      return wrap(`<div style="display:flex;flex-wrap:wrap;gap:16px;">${Array.from({ length: 5 }).map(() => '<div style="width:120px;height:48px;background:#eee;border-radius:8px;"></div>').join('')}</div>`);
    case 'team':
      return wrap(`<div style="display:flex;flex-wrap:wrap;gap:20px;">${Array.from({ length: 4 }).map(() => '<div style="text-align:center;width:120px;"><div style="width:80px;height:80px;border-radius:50%;background:#e5e5e5;margin:0 auto 8px;"></div><div style="height:9px;width:70%;background:#eee;border-radius:4px;margin:0 auto;"></div></div>').join('')}</div>`);
    case 'testimonials':
      return wrap(`<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;">${Array.from({ length: 3 }).map(() => '<div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:16px;background:#fff;"><div style="height:10px;background:#eee;border-radius:4px;margin-bottom:8px;"></div><div style="height:10px;width:80%;background:#eee;border-radius:4px;margin-bottom:12px;"></div><div style="height:9px;width:40%;background:#ddd;border-radius:4px;"></div></div>').join('')}</div>`);
    case 'stats':
      return wrap(`<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;text-align:center;">${Array.from({ length: 4 }).map(() => '<div><div style="font-size:1.8rem;font-weight:700;color:#333;">128</div><div style="height:9px;width:60%;background:#eee;border-radius:4px;margin:6px auto 0;"></div></div>').join('')}</div>`);
    case 'faq':
      return wrap(`<div>${Array.from({ length: 4 }).map(() => '<div style="border-bottom:1px solid rgba(0,0,0,.08);padding:14px 4px;"><div style="height:10px;width:60%;background:#e5e5e5;border-radius:4px;"></div></div>').join('')}</div>`);
    case 'contact_form':
    case 'booking_form':
      return wrap(`<div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:20px;background:#fff;max-width:520px;">
        ${Array.from({ length: name === 'booking_form' ? 4 : 3 }).map(() => '<div style="margin-bottom:12px;"><div style="height:8px;width:30%;background:#eee;border-radius:4px;margin-bottom:6px;"></div><div style="height:38px;border:1px solid rgba(0,0,0,.1);border-radius:8px;background:#fafafa;"></div></div>').join('')}
        <div style="height:42px;background:#333;border-radius:9999px;"></div>
      </div>`);
    case 'marketplace_search':
      return wrap(`<div style="height:46px;border:1px solid rgba(0,0,0,.12);border-radius:9999px;background:#fff;display:flex;align-items:center;padding:0 18px;color:#999;">🔍&nbsp;${attrs.placeholder || 'Rechercher...'}</div>`);
    case 'marketplace_categories':
      return wrap(`<div style="display:flex;flex-wrap:wrap;gap:10px;">${Array.from({ length: 6 }).map(() => '<div style="height:34px;width:110px;background:#eee;border-radius:9999px;"></div>').join('')}</div>`);
    case 'marketplace_sellers':
      return wrap(`<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;">${Array.from({ length: 3 }).map(() => '<div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:16px;background:#fff;"><div style="width:44px;height:44px;border-radius:50%;background:#e5e5e5;margin-bottom:10px;"></div><div style="height:10px;width:60%;background:#eee;border-radius:4px;"></div></div>').join('')}</div>`);
    default:
      return wrap(`<div style="border:1px dashed rgba(0,0,0,.2);border-radius:12px;padding:20px;text-align:center;color:#888;font:13px/1.5 sans-serif;">Bloc dynamique <strong>{{${name}}}</strong></div>`);
  }
}

function parseAttrs(raw: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /(\w+)="([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    attrs[m[1]] = m[2];
  }
  return attrs;
}

/** Remplace les template tags (ou les blocs React) par un rendu d'exemple. */
function renderPreviewContent(content: string): string {
  const schema = parseBlockSchema(content || '');
  if (schema) {
    return schema.blocks.map((b) => {
      const attrs: Record<string, string> = {};
      Object.entries(b.props || {}).forEach(([k, v]) => { attrs[k] = String(v ?? ''); });
      return blockPlaceholder(b.type, attrs);
    }).join('\n');
  }
  return content.replace(TAG_REGEX, (_full, name: string, rawAttrs: string) => {
    const known = THEME_TAGS.some((t) => t.name === name);
    if (!known) return `<span style="background:#fee;color:#c00;padding:1px 5px;border-radius:4px;font:12px monospace;">{{${name}}}</span>`;
    if (SIMPLE_TAG_SAMPLES[name] !== undefined) return SIMPLE_TAG_SAMPLES[name];
    return blockPlaceholder(name, parseAttrs(rawAttrs || ''));
  });
}

export default function LivePreviewPanel({ stylesheet, pages, pageDefs }: LivePreviewPanelProps) {
  const [activePreviewPage, setActivePreviewPage] = useState<string>('home');
  const [previewKey, setPreviewKey] = useState(0);
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  const activePage = useMemo(
    () => pages.find((p) => p.page_key === activePreviewPage),
    [pages, activePreviewPage]
  );

  const headerPage = useMemo(() => pages.find((p) => p.page_key === 'header'), [pages]);
  const footerPage = useMemo(() => pages.find((p) => p.page_key === 'footer'), [pages]);
  const headPage = useMemo(() => pages.find((p) => p.page_key === 'head'), [pages]);

  const previewBlobUrl = useMemo(() => {
    const render = (c?: string) => renderPreviewContent(c || '');

    const headTags = render(headPage?.content || '');
    const headerHtml = render(headerPage?.content || '');
    const footerHtml = render(footerPage?.content || '');

    // Une page de structure (head/header/navmenu/footer) est prévisualisée seule
    const isLayoutPage = ['head', 'header', 'navmenu', 'footer'].includes(activePreviewPage);
    const pageContent = render(activePage?.content || '');

    let body: string;
    if (isLayoutPage) {
      body = pageContent || '<p style="padding:40px;font:14px sans-serif;color:#999;">Cette page est vide</p>';
    } else {
      body = `${headerHtml}<main>${pageContent || '<p style="padding:40px;font:14px sans-serif;color:#999;">Cette page est vide</p>'}</main>${footerHtml}`;
    }

    const fullDoc = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${activePage?.title || 'Aperçu'}</title>
  <style>${stylesheet}</style>
  ${headTags}
</head>
<body>
  ${body || '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#999;"><p>Cette page est vide</p></div>'}
</body>
</html>`;

    const blob = new Blob([fullDoc], { type: 'text/html' });
    return URL.createObjectURL(blob);
  }, [stylesheet, activePage, activePreviewPage, headerPage, footerPage, headPage, previewKey]);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  const handleRefresh = () => setPreviewKey((k) => k + 1);

  const pagesWithContent = pages.filter((p) => p.content);
  const displayPages = pagesWithContent.length > 0 ? pagesWithContent : pageDefs;

  // Validation en direct des balises sur l'ensemble des pages du thème.
  const tagIssues = useMemo(() => {
    let invalid = 0;
    let foreign = 0;
    pages.forEach((p) => {
      invalid += findInvalidThemeTags(p.content || '').length;
      foreign += findForeignThemeSyntax(p.content || '').length;
    });
    return { invalid, foreign, total: invalid + foreign };
  }, [pages]);

  const deviceWidth = device === 'desktop' ? '100%' : device === 'tablet' ? '768px' : '390px';

  return (
    <div className="flex h-full">
      {/* Page selector sidebar */}
      <div className="w-[120px] sm:w-[180px] border-r border-background-200/70 bg-background-100/50 overflow-y-auto flex-shrink-0">
        <div className="p-2">
          <div className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold px-2 py-1.5">
            Pages à prévisualiser
          </div>
          {displayPages.map((item) => {
            const pageKey = 'page_key' in item ? item.page_key : item.key;
            const label = 'page_key' in item
              ? (pageDefs.find((d) => d.key === pageKey)?.label || pageKey)
              : item.label;
            const isSelected = activePreviewPage === pageKey;
            const hasContent = 'page_key' in item ? !!item.content : false;
            return (
              <button
                key={pageKey}
                onClick={() => setActivePreviewPage(pageKey)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer mb-0.5 ${
                  isSelected
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                <i className={`${'page_key' in item ? (pageDefs.find((d) => d.key === pageKey)?.icon || 'ri-file-line') : item.icon} text-sm flex-shrink-0`}></i>
                <span className="truncate">{label}</span>
                {hasContent && !isSelected && (
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0 ml-auto"></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview iframe */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between px-2 sm:px-3 py-2 border-b border-background-200/70 bg-background-100/50 flex-shrink-0 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider hidden sm:inline">Aperçu</span>
            <span className="text-[10px] text-foreground-400 bg-background-200/70 px-1.5 py-0.5 rounded truncate max-w-[90px] sm:max-w-none">
              {activePage?.title || activePreviewPage}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Validation des balises */}
            {tagIssues.total === 0 ? (
              <span className="hidden sm:flex items-center gap-1 text-[10px] font-medium text-accent-700 bg-accent-100 px-2 py-0.5 rounded-full" title="Toutes les balises sont valides">
                <i className="ri-check-double-line text-xs"></i>Balises valides
              </span>
            ) : (
              <span
                className="hidden sm:flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full"
                title={`${tagIssues.invalid} balise(s) inconnue(s), ${tagIssues.foreign} balise(s) non-Zifek — elles s'afficheront vides sur le site public`}
              >
                <i className="ri-error-warning-line text-xs"></i>
                {tagIssues.total} balise(s) à corriger
              </span>
            )}
            {/* Device switcher */}
            <div className="flex items-center bg-background-200/60 rounded-full p-0.5">
              {([
                { key: 'desktop', icon: 'ri-computer-line' },
                { key: 'tablet', icon: 'ri-tablet-line' },
                { key: 'mobile', icon: 'ri-smartphone-line' },
              ] as const).map((d) => (
                <button
                  key={d.key}
                  onClick={() => setDevice(d.key)}
                  title={d.key}
                  className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                    device === d.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-400 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${d.icon} text-xs`}></i>
                </button>
              ))}
            </div>
            <span className="text-[10px] text-foreground-400 hidden md:inline">
              {stylesheet.length.toLocaleString()} car. CSS
            </span>
            <button
              onClick={handleRefresh}
              className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
              title="Rafraîchir l'aperçu"
            >
              <i className="ri-refresh-line text-xs text-foreground-500"></i>
            </button>
          </div>
        </div>
        <div className="flex-1 bg-background-200/40 flex justify-center overflow-auto">
          <div
            className="bg-background-50 h-full transition-all"
            style={{ width: deviceWidth, maxWidth: '100%' }}
          >
            <iframe
              key={`${previewKey}-${device}`}
              src={previewBlobUrl}
              className="w-full h-full border-0"
              title="Aperçu du thème"
              sandbox="allow-scripts"
            />
          </div>
        </div>
      </div>
    </div>
  );
}